/**
 * components/landing/platform/sections/DataFlow.jsx — §3.5 How Everything Connects.
 *
 * Different from the Architecture section — this one shows the *user
 * journey* through the platform as an animated data flow.  The path
 * moves left-to-right on desktop, top-to-bottom on mobile.  A small
 * dot rides along the SVG path so the whole thing feels alive.
 */

import React from 'react'
import clsx from 'clsx'
import {
  LogIn, Bot, BookOpen, LineChart, Wand2, FlaskConical, Wallet, PieChart,
  BarChart3, Cog, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { DATA_FLOW } from '../data'

const ICONS = { LogIn, Bot, BookOpen, LineChart, Wand2, FlaskConical, Wallet, PieChart, BarChart3, Cog }
const TONE  = {
  orange: { hex: '#FFC107', rgb: '243,112,33' },
  green:  { hex: '#22c55e', rgb: '34,197,94' },
  blue:   { hex: '#3b82f6', rgb: '59,130,246' },
  purple: { hex: '#a855f7', rgb: '168,85,247' },
  cyan:   { hex: '#22d3ee', rgb: '34,211,238' },
}

export default function DataFlow() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-12">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>How everything connects</Eyebrow>
          <SectionHeading
            align="center"
            title={<>One <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">continuous data flow</span> — not ten disconnected apps.</>}
            subtitle="Follow an operator’s journey through the platform in real time. Every module below feeds the next — nothing has to be manually exported, transformed, or reconciled."
          />
        </div>

        {/* Desktop — horizontal flow */}
        <div className="hidden lg:block relative">
          <svg viewBox="0 0 1400 340" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="df-line" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"   stopColor="#FFC107" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#22c55e" stopOpacity="0.4" />
              </linearGradient>
            </defs>
            {/* main path */}
            <path
              id="df-path"
              d="M 60 170 C 200 60, 380 280, 540 170 S 900 280, 1060 170 S 1300 60, 1340 170"
              stroke="url(#df-line)" strokeWidth="2" strokeDasharray="4 8" fill="none"
              className="animate-brand-dash"
            />
            {/* moving packet */}
            <circle r="4" fill="#FFC107">
              <animateMotion dur="12s" repeatCount="indefinite">
                <mpath href="#df-path" />
              </animateMotion>
            </circle>

            {/* nodes evenly along the path (10 items → x from 90 → 1310) */}
            {DATA_FLOW.map((n, i) => {
              const x = 90 + (1220 * i) / (DATA_FLOW.length - 1)
              // sinusoidal y so nodes trace the wave path
              const wave = Math.sin((i / (DATA_FLOW.length - 1)) * Math.PI * 3) * 40
              const y = 170 + wave * (i % 2 === 0 ? 1 : -1) * 0
              const tone = TONE[n.tone] || TONE.orange
              const Icon = ICONS[n.iconKey] || Sparkles
              return (
                <g key={n.id} transform={`translate(${x} ${y})`}>
                  <circle r="32" fill="rgba(10,10,12,0.9)" stroke={tone.hex} strokeOpacity="0.55" strokeWidth="1.4" />
                  <circle r="29" fill={`${tone.hex}12`} />
                  <foreignObject x="-16" y="-16" width="32" height="32">
                    <div className="flex items-center justify-center w-full h-full" style={{ color: tone.hex }}>
                      <Icon strokeWidth={1.8} style={{ width: 16, height: 16 }} />
                    </div>
                  </foreignObject>
                  <text y="52" textAnchor="middle" fontSize="11" fontWeight="600" fill="rgba(255,255,255,0.9)">{n.label}</text>
                  <text y="66" textAnchor="middle" fontSize="9" fontFamily="monospace" fill="rgba(255,255,255,0.35)">step {String(i + 1).padStart(2, '0')}</text>
                </g>
              )
            })}
          </svg>
        </div>

        {/* Mobile / tablet — vertical stack with connector */}
        <ol className="lg:hidden relative pl-8">
          <div className="absolute left-3.5 top-2 bottom-2 w-px bg-white/10" />
          <div className="absolute left-3.5 top-2 w-px bg-gradient-to-b from-brand-gold to-[#22c55e]" style={{ height: '100%' }} />
          {DATA_FLOW.map((n, i) => {
            const tone = TONE[n.tone] || TONE.orange
            const Icon = ICONS[n.iconKey] || Sparkles
            return (
              <li key={n.id} className="relative mb-6 last:mb-0">
                <span
                  className="absolute -left-[22px] top-3 h-5 w-5 rounded-full border flex items-center justify-center"
                  style={{ borderColor: tone.hex, background: `rgba(10,10,12,0.9)`, color: tone.hex }}
                >
                  <Icon style={{ width: 10, height: 10 }} strokeWidth={2} />
                </span>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1">step {String(i + 1).padStart(2, '0')}</div>
                  <div className="text-base font-semibold text-white leading-tight">{n.label}</div>
                  <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{n.detail}</p>
                </div>
              </li>
            )
          })}
        </ol>

        {/* Desktop — detail row underneath the flow */}
        <div className="hidden lg:grid grid-cols-5 gap-3">
          {DATA_FLOW.map((n, i) => {
            const tone = TONE[n.tone] || TONE.orange
            return (
              <div key={n.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: tone.hex }}>step {String(i + 1).padStart(2, '0')}</div>
                <div className="text-[13px] font-semibold text-white mt-0.5 leading-tight">{n.label}</div>
                <p className="text-[11.5px] text-zinc-400 mt-1 leading-relaxed">{n.detail}</p>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
